import React, { useEffect, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { useQuery } from "@tanstack/react-query";
import { Link2, Copy, FlaskConical, Globe, Info } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useTenantSettings, useSaveSection } from "@/hooks/useTenantSettings";
import { Card } from "@/components/settings/tenant/sections";
import { TextField, SelectField, SwitchField } from "@/components/kit/fields";
import { Button } from "@/components/ui/button";

function LinkRow({ url, badge, badgeColor, icon: Icon, hint, testId }) {
  const copy = () => copyText(url, "Link copied");
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5" style={{ color: badgeColor }} />
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: badgeColor }}>{badge}</span>
      </div>
      <div className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2.5">
        <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <code className="flex-1 truncate font-mono text-xs text-foreground">{url}</code>
        <button onClick={copy} className="shrink-0 text-muted-foreground hover:text-foreground" data-testid={`${testId}-copy`}><Copy className="h-3.5 w-3.5" /></button>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function RegistrationLinks() {
  const { data } = useTenantSettings();
  const save = useSaveSection();
  const { data: platform } = useQuery({ queryKey: ["platform-public"], queryFn: async () => (await http.get("/platform-settings/public")).data });
  const { data: domData } = useQuery({ queryKey: ["domains"], queryFn: async () => (await http.get("/domains")).data });

  const [slug, setSlug] = useState("");
  const [savingSlug, setSavingSlug] = useState(false);
  useEffect(() => { if (data?.sections?.organization) setSlug(data.sections.organization.registration_slug || ""); }, [data]);

  if (!data) return null;
  const reg = data.sections.registration;
  const portalDomains = (domData?.items || []).filter((d) => (d.usages || []).includes("partner_panel") && d.status === "active");
  const postbackDomains = (domData?.items || []).filter((d) => (d.usages || []).includes("postback") && d.status === "active");
  const trackingDomains = (domData?.items || []).filter((d) => (d.usages || []).includes("tracking") && d.status === "active");
  const safeSlug = slug || "your-slug";

  // Single source of truth: the tenant portal is {partner portal domain || active platform domain}/portal/{slug}.
  const platformOrigin = platform?.platform_origin || (typeof window !== "undefined" ? window.location.origin : "");
  // Optional tenant-owned white-label Partner Portal domain (production).
  const prodDomain = reg.registration_domain && portalDomains.some((d) => d.domain === reg.registration_domain)
    ? reg.registration_domain : (portalDomains.find((d) => (d.defaults || {}).partner_panel)?.domain || portalDomains[0]?.domain || "");
  const portalOrigin = prodDomain ? `https://${prodDomain}` : platformOrigin;
  const portalUrl = `${portalOrigin}/portal/${safeSlug}`;

  // Postback URL uses the tenant's default Postback domain, else the active Platform Domain.
  const postbackDomain = postbackDomains.find((d) => (d.defaults || {}).postback)?.domain || postbackDomains[0]?.domain || "";
  const postbackUrl = postbackDomain ? `https://${postbackDomain}/api/postback` : `${platformOrigin}/api/postback`;
  // Tracking base uses the tenant's default Tracking domain, else the active Platform Domain.
  const trackingDomain = trackingDomains.find((d) => (d.defaults || {}).tracking)?.domain || trackingDomains[0]?.domain || "";
  const trackingUrl = trackingDomain ? `https://${trackingDomain}/api/r/{key}` : `${platformOrigin}/api/r/{key}`;

  const saveSlug = async () => {
    setSavingSlug(true);
    try { const r = await http.put("/tenant-settings/registration-slug", { slug }); setSlug(r.data.slug); toast.success("Registration slug updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSavingSlug(false); }
  };
  const togglePublic = async (v) => {
    await save.mutateAsync({ section: "registration", data: { ...reg, allow_public_registration: v } });
    toast.success(v ? "Public registration enabled" : "Public registration disabled");
  };
  const setRegDomain = async (v) => {
    if (v === "__none__") return;
    await save.mutateAsync({ section: "registration", data: { ...reg, registration_domain: v } });
    toast.success("Affiliate portal domain updated");
  };

  return (
    <Card title="Affiliate portal">
      <div className="space-y-5">
        <p className="text-xs text-muted-foreground">Your tenant has its own public affiliate portal (login · register · forgot password). Share these links with webmasters — not the admin app.</p>
        <div className="flex items-end gap-2">
          <TextField label="Portal slug (unique)" value={slug} onChange={setSlug} testId="reg-slug-input" className="flex-1" />
          <Button onClick={saveSlug} disabled={savingSlug} data-testid="reg-slug-save" className="h-10 bg-white text-black hover:bg-white/90">Save slug</Button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField label="Partner Portal domain" value={prodDomain} onChange={setRegDomain}
            options={portalDomains.length ? portalDomains.map((d) => d.domain) : [{ value: "__none__", label: "No Partner Portal domain — add one in Domains tab" }]} testId="reg-domain-select" />
          <div className="flex items-end"><SwitchField label="Public registration" checked={reg.allow_public_registration} onChange={togglePublic} testId="reg-public-toggle" /></div>
        </div>

        <div className="space-y-4 pt-1" data-testid="registration-links">
          <LinkRow testId="reg-portal-link" url={portalUrl} badge="Partner Portal URL" badgeColor="#22C55E" icon={Globe}
            hint="Used for affiliate login, registration and password recovery. Uses your Partner Portal domain if set, otherwise the platform's active domain — updates automatically." />
          <LinkRow testId="reg-postback-link" url={postbackUrl} badge="Affiliate postback URL" badgeColor="#A855F7" icon={Globe}
            hint={postbackDomain ? "Using your dedicated Postback domain." : "Give this to advertisers, e.g. …/api/postback?subid={subid}&status={status}&payout={payout}. Uses the platform domain until you connect a Postback domain."} />
          <LinkRow testId="reg-tracking-link" url={trackingUrl} badge="Tracking base URL" badgeColor="#EAB308" icon={Globe}
            hint={trackingDomain ? "Using your dedicated Tracking domain — used by new stream links." : "Stream links use the platform domain until you connect a Tracking domain."} />
          {prodDomain && (
            <LinkRow testId="reg-prod-link" url={`https://${prodDomain}`} badge="Custom Partner Portal domain" badgeColor="#3B82F6" icon={Globe}
              hint="Live once this domain's CNAME points to the platform and DNS + SSL are verified — serves the same portal." />
          )}
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-white/[0.06] bg-[#0A0A0A] px-3 py-2.5">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <p className="text-xs text-muted-foreground">
            The admin app is only for Super/Tenant Admins. Webmasters use the portal URL above — you never
            configure the platform domain; it's managed centrally and your link updates on its own.
          </p>
        </div>
      </div>
    </Card>
  );
}
